/** Motyw faktycznie namalowany na ekranie. */
export type Theme = 'light' | 'dark';

/** Ustawienie uzytkownika — `system` sledzi preferencje systemu operacyjnego. */
export type ThemePreference = Theme | 'system';

/** Klucz w `localStorage`. Ta sama wartosc jest uzyta w skrypcie w `index.html`. */
export const THEME_STORAGE_KEY = 'tenify.theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Odczytuje zapisane ustawienie motywu. Domyslnie `dark` — branding (zolte logo)
 * jest projektowany na ciemne tlo, wiec preferencji systemu celowo nie sledzimy;
 * jasny motyw wlacza sie dopiero swiadomym kliknieciem przelacznika.
 */
export function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'dark';
  } catch {
    // Tryb prywatny albo zablokowane ciasteczka — zostaje motyw domyslny.
    return 'dark';
  }
}

/** Zapisuje ustawienie. Dla `system` czysci wpis, zeby wrocic do preferencji systemowej. */
export function storePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') {
      localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  } catch {
    // Brak dostepu do storage nie moze wywrocic aplikacji.
  }
}

/** Zwraca motyw preferowany przez system operacyjny. */
export function getSystemTheme(): Theme {
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

/** Rozwiazuje ustawienie do konkretnego motywu. */
export function resolveTheme(preference: ThemePreference): Theme {
  return preference === 'system' ? getSystemTheme() : preference;
}

/** Nanosi motyw na dokument. Tokeny CSS reaguja na atrybut `data-theme`. */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}

/**
 * Subskrybuje zmiane motywu systemowego.
 *
 * Sygnatura celowo pasuje do `useSyncExternalStore` — preferencja systemu jest
 * stanem zewnetrznym, wiec czytamy ja w trakcie renderu zamiast kopiowac
 * do stanu Reacta efektem.
 */
export function subscribeToSystemTheme(onStoreChange: () => void): () => void {
  const media = window.matchMedia(DARK_QUERY);
  media.addEventListener('change', onStoreChange);
  return () => {
    media.removeEventListener('change', onStoreChange);
  };
}
